import { DCR_TYPE_LABELS } from './data_change_request.constants';
import { EDcrType } from '../types';
/**
 * Per-type field configuration driving both the create/edit form and the review summary.
 * A field's `name` doubles as the key written into dcr_intake__changes, so the payload shape
 * stays compatible with what the connector's automation already reads.
 */
export const DCR_TYPE_CONFIGS = {
    [EDcrType.UPDATE_PARTICIPANT_NUMBER]: {
        type: EDcrType.UPDATE_PARTICIPANT_NUMBER,
        label: DCR_TYPE_LABELS[EDcrType.UPDATE_PARTICIPANT_NUMBER],
        fields: [
            {
                name: 'dcr_intake__original_value',
                label: 'Original participant ID',
                type: 'readonly',
                derived: true,
                diffRole: 'old',
                diffLabel: 'Participant ID',
            },
            {
                name: 'dcr_intake__desired_value',
                label: 'New participant ID',
                type: 'text',
                required: true,
                warnOnParticipantIdFormat: true,
                mustDifferFrom: 'dcr_intake__original_value',
                diffRole: 'new',
                diffLabel: 'Participant ID',
            },
        ],
    },
    [EDcrType.INACTIVATE_RESPONSE]: {
        type: EDcrType.INACTIVATE_RESPONSE,
        label: DCR_TYPE_LABELS[EDcrType.INACTIVATE_RESPONSE],
        fields: [
            {
                name: 'dcr_intake__task_response_id',
                label: 'Response to inactivate',
                type: 'select',
                required: true,
                optionSource: 'participant_responses',
            },
            {
                name: 'dcr_intake__original_value',
                label: 'Current status',
                type: 'readonly',
                derived: true,
                dependsOn: ['dcr_intake__task_response_id'],
                diffRole: 'old',
                diffLabel: 'Response status',
            },
            {
                name: 'dcr_intake__desired_value',
                label: 'New status',
                type: 'readonly',
                derived: true,
                dependsOn: ['dcr_intake__task_response_id'],
                diffRole: 'new',
                diffLabel: 'Response status',
            },
        ],
        contextFields: ['dcr_intake__task_response_id'],
    },
    [EDcrType.UPDATE_SITE_RESPONSE]: {
        type: EDcrType.UPDATE_SITE_RESPONSE,
        label: DCR_TYPE_LABELS[EDcrType.UPDATE_SITE_RESPONSE],
        fields: [
            {
                name: 'dcr_intake__activity_id',
                label: 'Activity',
                type: 'select',
                required: true,
                optionSource: 'activities',
            },
            {
                name: 'dcr_intake__task_response_id',
                label: 'Response ID',
                type: 'select',
                required: true,
                optionSource: 'site_task_responses',
                dependsOn: ['dcr_intake__activity_id'],
            },
            {
                name: 'dcr_intake__step_response_id',
                label: 'Screen',
                type: 'select',
                required: true,
                optionSource: 'screens',
                dependsOn: ['dcr_intake__task_response_id'],
            },
            {
                name: 'dcr_intake__original_value',
                label: 'Original value',
                type: 'readonly',
                dependsOn: ['dcr_intake__step_response_id'],
                diffRole: 'old',
            },
            {
                name: 'dcr_intake__desired_value',
                label: 'New value',
                type: 'text',
                dynamicTypeFrom: 'screen',
                optionSource: 'screen_choices',
                required: true,
                dependsOn: ['dcr_intake__step_response_id'],
                mustDifferFrom: 'dcr_intake__original_value',
                diffRole: 'new',
            },
        ],
        contextFields: ['dcr_intake__activity_id', 'dcr_intake__task_response_id', 'dcr_intake__step_response_id'],
    },
    [EDcrType.UPDATE_PARTICIPANT_RESPONSE]: {
        type: EDcrType.UPDATE_PARTICIPANT_RESPONSE,
        label: DCR_TYPE_LABELS[EDcrType.UPDATE_PARTICIPANT_RESPONSE],
        fields: [
            {
                name: 'dcr_intake__notified_date',
                label: 'Date and time the participant notified the site of the change',
                type: 'datetime',
                required: true,
            },
            {
                name: 'dcr_intake__participant_initiated',
                label: 'The participant initiated this change',
                type: 'checkbox',
            },
            {
                name: 'dcr_intake__activity_id',
                label: 'Activity',
                type: 'select',
                required: true,
                optionSource: 'participant_activities',
            },
            {
                name: 'dcr_intake__task_response_id',
                label: 'Response ID',
                type: 'select',
                required: true,
                optionSource: 'epro_responses',
                dependsOn: ['dcr_intake__activity_id'],
            },
            {
                name: 'dcr_intake__step_response_id',
                label: 'Screen',
                type: 'select',
                required: true,
                optionSource: 'screens',
                dependsOn: ['dcr_intake__task_response_id'],
            },
            {
                name: 'dcr_intake__original_value',
                label: 'Original value',
                type: 'readonly',
                dependsOn: ['dcr_intake__step_response_id'],
                diffRole: 'old',
            },
            {
                name: 'dcr_intake__desired_value',
                label: 'New value',
                type: 'text',
                dynamicTypeFrom: 'screen',
                optionSource: 'screen_choices',
                required: true,
                dependsOn: ['dcr_intake__step_response_id'],
                mustDifferFrom: 'dcr_intake__original_value',
                diffRole: 'new',
            },
        ],
        contextFields: [
            'dcr_intake__notified_date',
            'dcr_intake__participant_initiated',
            'dcr_intake__activity_id',
            'dcr_intake__task_response_id',
            'dcr_intake__step_response_id',
        ],
    },
    [EDcrType.UPDATE_VISIT_DATE]: {
        type: EDcrType.UPDATE_VISIT_DATE,
        label: DCR_TYPE_LABELS[EDcrType.UPDATE_VISIT_DATE],
        fields: [
            {
                name: 'dcr_intake__visit_id',
                label: 'Visit',
                type: 'select',
                required: true,
                optionSource: 'visits',
            },
            {
                name: 'dcr_intake__original_value',
                label: 'Original value',
                type: 'readonly',
                dependsOn: ['dcr_intake__visit_id'],
                diffRole: 'old',
                diffLabel: 'Visit date',
            },
            {
                name: 'dcr_intake__desired_value',
                label: 'New value',
                type: 'date',
                required: true,
                dependsOn: ['dcr_intake__visit_id'],
                mustDifferFrom: 'dcr_intake__original_value',
                diffRole: 'new',
                diffLabel: 'Visit date',
            },
            {
                name: 'dcr_intake__update_activity_schedule',
                label: "Update the participant's activity schedule to match the new visit date",
                type: 'checkbox',
                dependsOn: ['dcr_intake__visit_id'],
            },
        ],
        contextFields: ['dcr_intake__visit_id', 'dcr_intake__update_activity_schedule'],
    },
    [EDcrType.INACTIVATE_PARTICIPANT]: {
        type: EDcrType.INACTIVATE_PARTICIPANT,
        label: DCR_TYPE_LABELS[EDcrType.INACTIVATE_PARTICIPANT],
        fields: [
            {
                name: 'dcr_intake__notes',
                label: 'Inactivate participant',
                type: 'info',
                infoTitle: 'Not the same as deactivating a participant',
                infoBody: "Use this when a participant record was created by mistake and isn't tied to a real study participant. If the participant is real but should stop participating, deactivate them instead.",
            },
        ],
    },
    [EDcrType.MOVE_PARTICIPANT_TO_ANOTHER_SITE]: {
        type: EDcrType.MOVE_PARTICIPANT_TO_ANOTHER_SITE,
        label: DCR_TYPE_LABELS[EDcrType.MOVE_PARTICIPANT_TO_ANOTHER_SITE],
        fields: [
            {
                name: 'dcr_intake__original_value',
                label: 'Original site number',
                type: 'readonly',
                diffRole: 'old',
                diffLabel: 'Site number',
            },
            {
                name: 'dcr_intake__desired_value',
                label: 'New site number',
                type: 'text',
                required: true,
                validateAgainst: 'sites',
                mustDifferFrom: 'dcr_intake__original_value',
                diffRole: 'new',
                diffLabel: 'Site number',
            },
            {
                name: 'dcr_intake__move_existing_responses',
                label: 'Move existing responses to the new site',
                type: 'checkbox',
                helperText: 'Ask the original site whether responses already submitted at this site should move with the participant to the new site.',
            },
        ],
        contextFields: ['dcr_intake__move_existing_responses'],
    },
    [EDcrType.MOVE_RESPONSES_TO_ANOTHER_VISIT]: {
        type: EDcrType.MOVE_RESPONSES_TO_ANOTHER_VISIT,
        label: DCR_TYPE_LABELS[EDcrType.MOVE_RESPONSES_TO_ANOTHER_VISIT],
        fields: [
            {
                name: 'dcr_intake__visit_id',
                label: 'Original visit',
                type: 'select',
                required: true,
                optionSource: 'visits',
                diffRole: 'old',
                diffLabel: 'Visit',
            },
            {
                name: 'dcr_intake__desired_visit_id',
                label: 'Desired visit',
                type: 'select',
                required: true,
                optionSource: 'desired_visits',
                dependsOn: ['dcr_intake__visit_id'],
                mustDifferFrom: 'dcr_intake__visit_id',
                diffRole: 'new',
                diffLabel: 'Visit',
            },
            {
                name: 'dcr_intake__activities_to_move',
                label: 'Activities to move',
                type: 'checkbox_list',
                required: true,
                optionSource: 'visit_activities',
                dependsOn: ['dcr_intake__desired_visit_id'],
            },
        ],
    },
    [EDcrType.OTHER]: {
        type: EDcrType.OTHER,
        label: DCR_TYPE_LABELS[EDcrType.OTHER],
        fields: [
            {
                name: 'dcr_intake__notes',
                label: 'Data change details',
                type: 'textarea',
                required: true,
                placeholder: 'Describe the data change being requested. Please do not include PII in the data change description.',
            },
        ],
        contextFields: ['dcr_intake__notes'],
    },
};
export const getDcrTypeConfig = (type) => type ? DCR_TYPE_CONFIGS[type] : undefined;
